import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Search, ArrowRight, RefreshCw, Package, AlertTriangle, 
  ArrowUpDown, ArrowUp, ArrowDown, Truck, Building2, Download
} from 'lucide-react';
import { toast } from 'sonner';
import LoadingOverlay from '../components/LoadingOverlay';
import ErrorState from '../components/ErrorState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TransferRecommendations = () => {
  const { getAuthHeaders } = useAuth();
  const [recommendations, setRecommendations] = useState([]);
  const [summary, setSummary] = useState({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  // Filters
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sourceDepotFilter, setSourceDepotFilter] = useState('all');
  const [targetDepotFilter, setTargetDepotFilter] = useState('all');
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: 'priority', direction: 'asc' });

  const fetchRecommendations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/transfer-recommendations`, { 
        headers: getAuthHeaders() 
      });
      setRecommendations(response.data.recommendations || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error('Error fetching transfer recommendations:', error);
      if (error.response?.status === 403) {
        setError('Access denied. Only Admin and Jaipur depot users can view transfer recommendations.');
      } else {
        setError('Unable to load transfer recommendations. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/transfer-recommendations/refresh`, {}, { 
        headers: getAuthHeaders() 
      });
      toast.success('Recommendations refreshed successfully');
      await fetchRecommendations();
    } catch (error) {
      console.error('Error refreshing recommendations:', error);
      toast.error(error.response?.data?.detail || 'Failed to refresh recommendations');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchRecommendations();
  }, []);

  // Get unique depots for filters
  const sourceDepots = useMemo(() => {
    const depots = [...new Set(recommendations.map(r => r.source_depot_name))];
    return depots.sort();
  }, [recommendations]);

  const targetDepots = useMemo(() => {
    const depots = [...new Set(recommendations.map(r => r.target_depot_name))];
    return depots.sort();
  }, [recommendations]);

  // Filter and sort recommendations
  const filteredRecommendations = useMemo(() => {
    let items = [...recommendations];
    
    // Search filter
    if (search.trim()) {
      const query = search.toLowerCase();
      items = items.filter(item => 
        item.item_name.toLowerCase().includes(query) ||
        item.sku.toLowerCase().includes(query) ||
        item.source_depot_name.toLowerCase().includes(query) ||
        item.target_depot_name.toLowerCase().includes(query)
      );
    }
    
    // Priority filter
    if (priorityFilter !== 'all') {
      items = items.filter(item => item.priority === priorityFilter);
    }
    
    // Source depot filter
    if (sourceDepotFilter !== 'all') {
      items = items.filter(item => item.source_depot_name === sourceDepotFilter);
    }
    
    // Target depot filter
    if (targetDepotFilter !== 'all') {
      items = items.filter(item => item.target_depot_name === targetDepotFilter);
    }
    
    // Sort
    if (sortConfig.key) {
      items.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        // Priority special handling (high before medium)
        if (sortConfig.key === 'priority') {
          aVal = aVal === 'high' ? 0 : 1;
          bVal = bVal === 'high' ? 0 : 1;
        }
        
        if (typeof aVal === 'number') {
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }
        return sortConfig.direction === 'asc' 
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal));
      });
    }
    
    return items;
  }, [recommendations, search, priorityFilter, sourceDepotFilter, targetDepotFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) {
      return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1" /> 
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  const exportToExcel = () => {
    const exportData = filteredRecommendations.map(rec => ({
      'Item Name': rec.item_name,
      'SKU': rec.sku,
      'From Depot': rec.source_depot_name,
      'Source Stock': rec.source_stock,
      'Source Avg Sale/Mo': rec.source_avg_sales,
      'Source Coverage (Mo)': rec.source_coverage,
      'To Depot': rec.target_depot_name,
      'Target Stock': rec.target_stock,
      'Target Avg Sale/Mo': rec.target_avg_sales,
      'Target Coverage (Mo)': rec.target_coverage,
      'Recommended Qty': rec.recommended_qty,
      'Priority': rec.priority,
      'Reason': rec.reason
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 30 }, // Item Name
      { wch: 15 }, // SKU
      { wch: 15 }, // From Depot
      { wch: 12 }, // Source Stock
      { wch: 18 }, // Source Avg Sale/Mo
      { wch: 18 }, // Source Coverage
      { wch: 15 }, // To Depot
      { wch: 12 }, // Target Stock
      { wch: 18 }, // Target Avg Sale/Mo
      { wch: 18 }, // Target Coverage
      { wch: 15 }, // Recommended Qty
      { wch: 10 }, // Priority
      { wch: 50 }, // Reason
    ];
    
    XLSX.utils.book_append_sheet(wb, ws, 'Transfer Recommendations');
    
    // Generate filename with date
    const filename = `transfer_recommendations_${new Date().toISOString().split('T')[0]}.xlsx`;
    
    // Download
    XLSX.writeFile(wb, filename);
    toast.success('Recommendations exported to Excel');
  };

  if (loading) return <LoadingOverlay message="Loading recommendations..." />;
  if (error) return <ErrorState message={error} onRetry={fetchRecommendations} />;

  return (
    <div className="space-y-6" data-testid="transfer-recommendations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Stock Transfer Recommendations</h1>
          <p className="text-sm text-slate-500 mt-1">
            Recommendations to balance stock across depots based on average sales
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={exportToExcel}
            disabled={filteredRecommendations.length === 0}
            data-testid="export-recommendations-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          <Button 
            onClick={handleRefresh} 
            disabled={refreshing}
            data-testid="refresh-recommendations-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Truck className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{summary.total_recommendations || 0}</p>
                <p className="text-xs text-slate-500">Total Transfers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{summary.high_priority || 0}</p>
                <p className="text-xs text-slate-500">High Priority</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{summary.total_units_to_transfer?.toLocaleString() || 0}</p>
                <p className="text-xs text-slate-500">Units to Transfer</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{summary.unique_items || 0}</p>
                <p className="text-xs text-slate-500">Unique Items</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Search by item name, SKU, or depot..." 
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-recommendations"
              />
            </div>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-full md:w-[150px]" data-testid="priority-filter">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sourceDepotFilter} onValueChange={setSourceDepotFilter}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="source-depot-filter">
                <SelectValue placeholder="Source Depot" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Source Depots</SelectItem>
                {sourceDepots.map(depot => (
                  <SelectItem key={depot} value={depot}>{depot}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={targetDepotFilter} onValueChange={setTargetDepotFilter}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="target-depot-filter">
                <SelectValue placeholder="Target Depot" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Target Depots</SelectItem>
                {targetDepots.map(depot => (
                  <SelectItem key={depot} value={depot}>{depot}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="text-sm text-slate-500">
        Showing {filteredRecommendations.length} of {recommendations.length} recommendations
      </div>

      {/* Recommendations Table */}
      {filteredRecommendations.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Recommendations</h3>
            <p className="text-slate-500 max-w-md mx-auto">
              {recommendations.length === 0 
                ? "Stock levels are balanced across all depots, or sales data hasn't been uploaded yet."
                : "No recommendations match your current filters."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="recommendations-table">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th 
                      className="text-left p-4 text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort('priority')}
                    >
                      <div className="flex items-center">
                        Priority
                        <SortIcon column="priority" />
                      </div>
                    </th>
                    <th 
                      className="text-left p-4 text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort('item_name')}
                    >
                      <div className="flex items-center">
                        Item
                        <SortIcon column="item_name" />
                      </div>
                    </th>
                    <th 
                      className="text-left p-4 text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort('source_depot_name')}
                    >
                      <div className="flex items-center">
                        From Depot
                        <SortIcon column="source_depot_name" />
                      </div>
                    </th>
                    <th 
                      className="text-left p-4 text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort('target_depot_name')}
                    >
                      <div className="flex items-center">
                        To Depot
                        <SortIcon column="target_depot_name" />
                      </div>
                    </th>
                    <th 
                      className="text-right p-4 text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort('recommended_qty')}
                    >
                      <div className="flex items-center justify-end">
                        Transfer Qty
                        <SortIcon column="recommended_qty" />
                      </div>
                    </th>
                    <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredRecommendations.map((rec) => (
                    <tr 
                      key={rec.id} 
                      className="hover:bg-slate-50 transition-colors"
                      data-testid={`recommendation-row-${rec.id}`}
                    >
                      <td className="p-4">
                        <Badge 
                          variant={rec.priority === 'high' ? 'destructive' : 'secondary'}
                          className={rec.priority === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}
                        >
                          {rec.priority === 'high' ? 'High' : 'Medium'}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-slate-900">{rec.item_name}</p>
                          <p className="text-xs text-slate-500">{rec.sku}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{rec.source_depot_name}</p>
                            <p className="text-xs text-slate-500">
                              Stock: {rec.source_stock} | Avg Sale: {rec.source_avg_sales}/mo | {rec.source_coverage} mo coverage
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <ArrowRight className="w-4 h-4 text-slate-400" />
                          <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-orange-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{rec.target_depot_name}</p>
                            <p className="text-xs text-slate-500">
                              Stock: {rec.target_stock} | Avg Sale: {rec.target_avg_sales}/mo | {rec.target_coverage} mo coverage
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <span className="text-lg font-bold text-blue-600">
                          {rec.recommended_qty.toLocaleString()}
                        </span>
                        <span className="text-slate-500 ml-1">units</span>
                      </td>
                      <td className="p-4">
                        <p className="text-sm text-slate-600 max-w-xs">{rec.reason}</p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TransferRecommendations;
